/**
 * TopbarChatButton — iter413i
 * Compact chat dropdown for the top bar. Replaces the previous full-page link
 * (which felt heavy for quick replies). Layout mirrors the Admin2 ChatWidget:
 *   • Header with unread badge + "Plný chat" deep-link.
 *   • Tabs: Místnosti / Lidé (online presence).
 *   • Click a room → expanded view with last 5 messages + quick reply form.
 *   • Click a person in "Lidé" → creates / opens a DM.
 *   • Delete chat + Call user buttons in the open-room header.
 * Talks to the same /api/v1/chat/* endpoints used by the widget.
 */
import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import {
  MessageCircle, X, ChevronLeft, ChevronRight, Send, Trash2,
  Phone, Users as UsersIcon, Loader2,
} from "lucide-react";
import { toast } from "sonner";
import { useWS } from "../contexts/WebSocketContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_V1 = `${BACKEND_URL}/api/v1`;

export default function TopbarChatButton() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState("rooms"); // "rooms" | "users"
  const [rooms, setRooms] = useState([]);
  const [users, setUsers] = useState([]);
  const [me, setMe] = useState(null);
  const [openRoomId, setOpenRoomId] = useState(null);
  const [openRoom, setOpenRoom] = useState(null);
  const [messages, setMessages] = useState([]);
  const [reply, setReply] = useState("");
  const [sending, setSending] = useState(false);
  const ws = useWS();
  const dropdownRef = useRef(null);

  const load = React.useCallback(() => {
    axios.get(`${API_V1}/chat/rooms`).then((r) => setRooms(r.data?.rooms || [])).catch(() => {});
    axios.get(`${API_V1}/chat/users`).then((r) => setUsers(r.data?.users || [])).catch(() => {});
    if (!me) axios.get(`${API_V1}/auth/me`).then((r) => setMe(r.data)).catch(() => {});
  }, [me]);

  useEffect(() => {
    if (!open) return;
    load();
    let unsub = null;
    if (ws?.subscribe) {
      unsub = ws.subscribe("chat", () => {
        load();
        if (openRoomId) {
          axios.get(`${API_V1}/chat/rooms/${openRoomId}/messages?limit=5`)
            .then((r) => setMessages((r.data?.messages || []).slice(-5))).catch(() => {});
        }
      });
    }
    const t = setInterval(load, 30_000);
    return () => { clearInterval(t); unsub?.(); };
  }, [open, ws, openRoomId, load]);

  // Always poll unread count in the background (every 60s) so the badge stays fresh.
  useEffect(() => {
    const pollUnread = () => {
      axios.get(`${API_V1}/chat/rooms`).then((r) => setRooms(r.data?.rooms || [])).catch(() => {});
    };
    pollUnread();
    const t = setInterval(pollUnread, 60_000);
    return () => clearInterval(t);
  }, []);

  const totalUnread = rooms.reduce((s, r) => s + (r.unread_count || 0), 0);
  const onlineCount = users.filter((u) => u.status === "online" && !u.is_me).length;

  const expandRoom = async (roomId) => {
    setOpenRoomId(roomId);
    setMessages([]); setReply("");
    try {
      const rr = await axios.get(`${API_V1}/chat/rooms/${roomId}`);
      setOpenRoom(rr.data || null);
      const r = await axios.get(`${API_V1}/chat/rooms/${roomId}/messages?limit=5`);
      setMessages((r.data?.messages || []).slice(-5));
    } catch { /* noop */ }
  };

  const sendQuickReply = async () => {
    const t = (reply || "").trim();
    if (!t || !openRoomId || sending) return;
    setSending(true);
    try {
      await axios.post(`${API_V1}/chat/rooms/${openRoomId}/messages`, { content: t });
      setReply("");
      const r = await axios.get(`${API_V1}/chat/rooms/${openRoomId}/messages?limit=5`);
      setMessages((r.data?.messages || []).slice(-5));
      load();
    } catch { /* noop */ }
    finally { setSending(false); }
  };

  const startDmWith = async (otherUserId) => {
    if (!me || otherUserId === me.user_id) return;
    try {
      const r = await axios.post(`${API_V1}/chat/rooms`, { type: "dm", members: [otherUserId] });
      const newRoom = r.data?.room;
      if (newRoom?.room_id) { setTab("rooms"); load(); expandRoom(newRoom.room_id); }
    } catch (e) { toast.error("Nepodařilo se otevřít chat"); }
  };

  const deleteOpenRoom = async () => {
    if (!openRoomId) return;
    if (!window.confirm("Opravdu smazat tento chat?")) return;
    try {
      await axios.delete(`${API_V1}/chat/rooms/${openRoomId}`);
      setOpenRoomId(null); setOpenRoom(null); setMessages([]); load();
    } catch (e) { toast.error("Chyba: " + (e.response?.data?.detail || e.message)); }
  };

  const callMember = async (member) => {
    if (!member) return;
    const phone = member.phone || "";
    if (phone) {
      try {
        const r = await axios.post(`${API_V1}/calls/dial`, { to_number: phone, from_user_id: me?.user_id });
        if (r.data?.ok) { toast.success("Volání zahájeno"); return; }
      } catch { /* fall through to tel: */ }
      window.open(`tel:${phone}`, "_self");
      return;
    }
    if (member.email) {
      window.open(`https://teams.microsoft.com/l/call/0/0?users=${encodeURIComponent(member.email)}`, "_blank");
    } else {
      toast.error("Uživatel nemá telefon ani e-mail");
    }
  };

  const otherMember = openRoom && openRoom.type === "dm"
    ? (openRoom.member_details || []).find((m) => m.user_id !== me?.user_id)
    : null;

  const goFullChat = () => {
    setOpen(false);
    navigate(openRoomId ? `/admin/chat?room=${openRoomId}` : "/admin/chat");
  };

  return (
    <div className="relative">
      <button onClick={() => setOpen(!open)}
        className="relative p-1.5 rounded-lg text-slate-400 hover:text-violet-600 hover:bg-slate-100/80 transition-all duration-200"
        data-testid="topbar-chat-btn"
        title="Chat">
        <MessageCircle className="w-4 h-4" />
        {totalUnread > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-4 h-4 px-1 bg-violet-500 text-white text-[9px] font-bold rounded-full flex items-center justify-center animate-pulse"
                data-testid="topbar-chat-unread-badge">
            {totalUnread > 9 ? "9+" : totalUnread}
          </span>
        )}
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div ref={dropdownRef}
               data-testid="topbar-chat-dropdown"
               className="absolute right-0 top-full mt-2 w-[360px] max-h-[520px] flex flex-col bg-white/95 backdrop-blur-xl border border-slate-200/60 rounded-2xl shadow-2xl z-50 overflow-hidden origin-top-right">
            {/* Header */}
            <div className="flex items-center justify-between px-3 py-2.5 border-b border-slate-100/80 bg-slate-50/50">
              <h3 className="text-sm font-bold text-slate-800 inline-flex items-center gap-1.5">
                <MessageCircle className="w-3.5 h-3.5 text-violet-500" />
                Chat
                {totalUnread > 0 && (
                  <span className="text-[10px] bg-violet-500 text-white px-1.5 rounded-full">{totalUnread}</span>
                )}
              </h3>
              <div className="flex items-center gap-0.5">
                {openRoomId && otherMember && (
                  <button onClick={() => callMember(otherMember)}
                          title={otherMember.phone ? `Zavolat ${otherMember.phone}` : "Zavolat přes Teams"}
                          data-testid="topbar-chat-call-btn"
                          className="p-1 rounded hover:bg-emerald-100 text-emerald-500">
                    <Phone className="w-3.5 h-3.5" />
                  </button>
                )}
                {openRoomId && (
                  <button onClick={deleteOpenRoom}
                          title="Smazat chat"
                          data-testid="topbar-chat-delete-btn"
                          className="p-1 rounded hover:bg-red-100 text-red-500">
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                )}
                <button onClick={goFullChat}
                        title="Plný chat"
                        data-testid="topbar-chat-fullchat-btn"
                        className="text-[10px] px-2 py-1 rounded-full bg-violet-50 hover:bg-violet-100 text-violet-700 font-medium">
                  Plný chat
                </button>
                <button onClick={() => setOpen(false)} className="text-slate-400 hover:text-slate-600 p-1 rounded-full hover:bg-slate-100 transition-colors">
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>

            {/* Tabs (only when no room is open) */}
            {!openRoomId && (
              <div className="flex border-b border-slate-200/60 text-[11px] font-semibold">
                <button onClick={() => setTab("rooms")}
                        data-testid="topbar-chat-tab-rooms"
                        className={`flex-1 py-2 transition-colors ${tab === "rooms" ? "text-violet-600 border-b-2 border-violet-500 bg-white" : "text-slate-400 hover:text-slate-600"}`}>
                  Místnosti ({rooms.length})
                </button>
                <button onClick={() => setTab("users")}
                        data-testid="topbar-chat-tab-users"
                        className={`flex-1 py-2 transition-colors ${tab === "users" ? "text-emerald-600 border-b-2 border-emerald-500 bg-white" : "text-slate-400 hover:text-slate-600"}`}>
                  Lidé ({onlineCount} online)
                </button>
              </div>
            )}

            {/* Back button row when room is open */}
            {openRoomId && (
              <div className="flex items-center gap-1 px-2 py-1.5 border-b border-slate-200/60 bg-slate-50/30">
                <button onClick={() => { setOpenRoomId(null); setOpenRoom(null); }}
                        data-testid="topbar-chat-back-btn"
                        className="inline-flex items-center gap-1 text-[11px] text-slate-500 hover:text-slate-800 px-1.5 py-0.5 rounded hover:bg-slate-100">
                  <ChevronLeft className="w-3 h-3" /> Zpět
                </button>
                <span className="text-[11px] font-medium text-slate-700 truncate flex-1">
                  {openRoom?.display_name || openRoom?.name || "Chat"}
                </span>
              </div>
            )}

            {/* Body */}
            <div className="flex-1 min-h-0 overflow-y-auto">
              {/* ROOMS LIST */}
              {!openRoomId && tab === "rooms" && (rooms.length === 0 ? (
                <div className="text-center py-10 text-slate-400 px-4">
                  <MessageCircle className="w-6 h-6 mx-auto mb-2 opacity-30" />
                  <p className="text-xs">Žádné místnosti</p>
                  <p className="text-[10px] mt-1">Otevři tab „Lidé“ a klikni na uživatele.</p>
                </div>
              ) : (
                rooms.slice(0, 20).map((r) => {
                  const ids = (r.members || []).filter(Boolean);
                  const online = ids.some((uid) => users.find((u) => u.user_id === uid)?.status === "online");
                  return (
                    <button key={r.room_id} onClick={() => expandRoom(r.room_id)}
                            data-testid={`topbar-chat-room-${r.room_id}`}
                            className="w-full text-left px-3 py-2 flex items-center gap-2 hover:bg-violet-50/40 border-b border-slate-100 last:border-b-0">
                      <div className="relative shrink-0">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center ${r.unread_count > 0 ? "bg-violet-500 text-white" : "bg-slate-100 text-slate-500"}`}>
                          <UsersIcon className="w-3.5 h-3.5" />
                        </div>
                        <span className={`absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full ring-2 ring-white ${online ? "bg-emerald-400" : "bg-slate-300"}`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-1">
                          <span className="text-xs font-semibold text-slate-800 truncate">{r.display_name || r.name || "Chat"}</span>
                          {r.unread_count > 0 && (
                            <span className="text-[9px] font-bold px-1.5 rounded-full bg-violet-600 text-white shrink-0">{r.unread_count}</span>
                          )}
                        </div>
                        <div className="text-[10px] text-slate-500 truncate">
                          {r.last_message?.sender_name && <span className="text-slate-600">{r.last_message.sender_name}: </span>}
                          {r.last_message?.content || r.last_message_preview || "—"}
                        </div>
                      </div>
                      <ChevronRight className="w-3 h-3 text-slate-300" />
                    </button>
                  );
                })
              ))}

              {/* USERS LIST */}
              {!openRoomId && tab === "users" && (users.length === 0 ? (
                <div className="text-center py-10 text-slate-400 px-4">
                  <Loader2 className="w-5 h-5 mx-auto mb-2 animate-spin opacity-50" />
                  <p className="text-xs">Načítám uživatele…</p>
                </div>
              ) : (
                users.filter((u) => !u.is_me).map((u) => (
                  <button key={u.user_id} onClick={() => startDmWith(u.user_id)}
                          data-testid={`topbar-chat-user-${u.user_id}`}
                          title={u.email}
                          className="w-full text-left px-3 py-2 flex items-center gap-2 hover:bg-emerald-50/40 border-b border-slate-100 last:border-b-0">
                    <div className="relative shrink-0">
                      <div className="w-8 h-8 rounded-full bg-gradient-to-br from-violet-400/30 to-fuchsia-400/30 flex items-center justify-center text-[10px] font-bold uppercase text-slate-700">
                        {(u.name || u.email || "?").slice(0, 2)}
                      </div>
                      <span className={`absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full ring-2 ring-white ${u.status === "online" ? "bg-emerald-400" : "bg-slate-300"}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-xs font-semibold text-slate-800 truncate">{u.name || u.email}</div>
                      <div className="text-[10px] text-slate-500 truncate">
                        {u.status === "online" ? "Online" : (u.last_seen
                          ? "Naposledy " + new Date(u.last_seen).toLocaleString("cs-CZ", { hour: "2-digit", minute: "2-digit", day: "numeric", month: "numeric" })
                          : "Offline")}
                      </div>
                    </div>
                    {u.phone && (
                      <Phone onClick={(e) => { e.stopPropagation(); callMember(u); }}
                             className="w-3.5 h-3.5 text-emerald-400 hover:text-emerald-600" />
                    )}
                  </button>
                ))
              ))}

              {/* OPENED ROOM — last 5 messages */}
              {openRoomId && (
                <div className="px-3 py-2 space-y-1.5">
                  {messages.length === 0 ? (
                    <p className="text-[11px] text-slate-400 text-center py-4">Žádné zprávy</p>
                  ) : (
                    messages.map((m) => (
                      <div key={m.message_id}
                           className={`text-[11px] p-1.5 rounded-lg max-w-[85%] ${m.sender_user_id === me?.user_id ? "bg-violet-100 text-violet-900 ml-auto" : "bg-slate-100 text-slate-800"}`}
                           data-testid={`topbar-chat-msg-${m.message_id}`}>
                        {m.sender_user_id !== me?.user_id && (
                          <div className="text-[9px] font-semibold text-slate-500 mb-0.5">{m.sender_name || m.sender_user_id}</div>
                        )}
                        <div className="whitespace-pre-wrap break-words">{m.content}</div>
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>

            {/* Quick reply input */}
            {openRoomId && (
              <form onSubmit={(e) => { e.preventDefault(); sendQuickReply(); }}
                    className="px-2 py-2 border-t border-slate-200/60 bg-slate-50/30 flex items-center gap-1.5"
                    data-testid="topbar-chat-reply-form">
                <input value={reply} onChange={(e) => setReply(e.target.value)} placeholder="Rychlá odpověď…"
                       className="flex-1 text-xs px-2.5 py-1.5 rounded-lg border border-slate-200 bg-white focus:outline-none focus:ring-2 focus:ring-violet-200 focus:border-violet-300"
                       data-testid="topbar-chat-reply-input" />
                <button type="submit" disabled={sending || !reply.trim()}
                        className="p-1.5 rounded-lg bg-violet-500 hover:bg-violet-600 text-white disabled:opacity-40 disabled:cursor-not-allowed"
                        data-testid="topbar-chat-send-btn">
                  {sending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                </button>
              </form>
            )}
          </div>
        </>
      )}
    </div>
  );
}
